import * as Sentry from '@sentry/browser';

import auth from 'utils/auth';
import { getSubdomain } from 'utils/browser';
import { getEnvConfig } from 'utils/getEnvConfig';

export class CompanionApiError extends Error {
  constructor(readonly status: number, readonly body: unknown) {
    super(`Companion request failed with status ${status}`);
  }
}

/** The Companion answers on its own host (`AGENT_BASE`, e.g. chat.workpath.com): same site, different origin. */
const companionBase = (): string =>
  (getEnvConfig('AGENT_BASE') ?? '').replace(/\/$/, '');

export const companionUrl = (path: string): string =>
  `${companionBase()}${path}`;

let sessionReady = false;
let pendingLogin: Promise<void> | null = null;

// Chrome reports a request cancelled by leaving the page as "Failed to fetch", not as an abort
let pageHidden = false;
window.addEventListener('pagehide', () => {
  pageHidden = true;
});
window.addEventListener('pageshow', () => {
  pageHidden = false;
});

// a transport failure (offline, DNS, CORS) never reaches the agent, so report it here — FE-only
const fetchOrReport: typeof fetch = async (input, init) => {
  try {
    return await fetch(input, init);
  } catch (e) {
    const isAbort = e instanceof DOMException && e.name === 'AbortError';
    if (!pageHidden && !isAbort) {
      Sentry.captureException(e);
    }
    throw e;
  }
};

const login = async (): Promise<void> => {
  const response = await fetchOrReport(companionUrl('/api/auth/login'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_token: auth.getToken(),
      account: getSubdomain(),
    }),
  });
  if (!response.ok) {
    throw new CompanionApiError(response.status, undefined);
  }
  sessionReady = true;
};

export const ensureSession = (): Promise<void> => {
  if (sessionReady) return Promise.resolve();
  if (!pendingLogin) {
    pendingLogin = login().finally(() => {
      pendingLogin = null;
    });
  }
  return pendingLogin;
};

export const resetCompanionSession = (): void => {
  sessionReady = false;
  pendingLogin = null;
};

/**
 * The agent is same-site but cross-origin, so the session cookie must be sent explicitly
 * on every call. A 401 means it expired: exchange the Workpath token we still hold for a
 * fresh one and replay the request once.
 */
export const companionFetch: typeof fetch = async (input, init) => {
  const send = () => fetchOrReport(input, { ...init, credentials: 'include' });

  await ensureSession();
  const response = await send();
  if (response.status !== 401) return response;

  sessionReady = false;
  await ensureSession();
  return send();
};

const readBody = (response: Response): Promise<unknown> =>
  response.json().catch(() => undefined);

export const request = async <T = void>(
  path: string,
  init?: RequestInit
): Promise<T> => {
  const response = await companionFetch(companionUrl(path), init);
  if (!response.ok) {
    throw new CompanionApiError(response.status, await readBody(response));
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
};
